import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './ProfilePanel.module.css';
import { UserIcon, LogOutIcon, EyeIcon, EyeOffIcon, BookIcon } from './Icons';
import { useAutoSave } from '../hooks/useAutoSave';

const ProfilePanel = ({ isOpen, onClose, onSave, initialProfile }) => {
  const { isAuthenticated, user } = useAuth0();
  
  const [profile, setProfile] = useState({
    gpa: '',
    sat: '',
    act: '',
    classRank: '',
    gender: '',
    ethnicity: '',
  });

  // Auto-save academic profile
  useAutoSave('academicProfile', profile);

  const [validation, setValidation] = useState({});
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [completionProgress, setCompletionProgress] = useState(0);
  const autoSaveTimeoutRef = useRef(null);

  // Body scroll lock when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Class rank options for the slider
  const classRankOptions = [
    { value: 'Top 1%', label: 'Top 1%', percentage: 1 },
    { value: 'Top 5%', label: 'Top 5%', percentage: 5 },
    { value: 'Top 10%', label: 'Top 10%', percentage: 10 },
    { value: 'Top 25%', label: 'Top 25%', percentage: 25 },
    { value: 'Top 50%', label: 'Top 50%', percentage: 50 },
    { value: 'Bottom 50%', label: 'Bottom 50%', percentage: 100 },
    { value: 'Unknown', label: "I don't know", percentage: null }
  ];

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Non-binary', label: 'Non-binary' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  const ethnicityOptions = [
    { value: 'Asian', label: 'Asian' },
    { value: 'Black or African American', label: 'Black or African American' },
    { value: 'Hispanic or Latino', label: 'Hispanic or Latino' },
    { value: 'Native American or Alaska Native', label: 'Native American or Alaska Native' },
    { value: 'Native Hawaiian or Other Pacific Islander', label: 'Native Hawaiian or Other Pacific Islander' },
    { value: 'White', label: 'White' },
    { value: 'Two or more races', label: 'Two or more races' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  // Calculate completion progress
  useEffect(() => {
    const fields = ['gpa', 'sat', 'act', 'classRank', 'gender', 'ethnicity'];
    const totalFields = fields.length;
    const filledFields = fields.filter(field => {
      const value = profile[field];
      return value && value.toString().trim() !== '';
    }).length;
    
    const progress = Math.round((filledFields / totalFields) * 100);
    setCompletionProgress(progress);
  }, [profile]);

  // Initialize profile
  useEffect(() => {
    if (initialProfile) {
      setProfile(initialProfile);
    } else {
      setProfile({
        gpa: '',
        sat: '',
        act: '',
        classRank: '',
        gender: '',
        ethnicity: '',
      });
    }
  }, [initialProfile, isOpen]);

  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'gpa':
        if (value && (parseFloat(value) < 0 || parseFloat(value) > 4.0)) {
          return 'GPA must be between 0.0 and 4.0';
        }
        break;
      case 'sat':
        if (value && (parseInt(value) < 400 || parseInt(value) > 1600)) {
          return 'SAT score must be between 400 and 1600';
        }
        break;
      case 'act':
        if (value && (parseInt(value) < 1 || parseInt(value) > 36)) {
          return 'ACT score must be between 1 and 36';
        }
        break;
      default:
        return null;
    }
    return null;
  };

  // Auto-save functionality - saves to localStorage without closing panel
  const autoSave = useCallback((newProfile) => {
    setIsAutoSaving(true);
    
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      // Only save to localStorage for auto-save, don't trigger onSave callback
      localStorage.setItem('academicProfile', JSON.stringify(newProfile));
      // Dispatch custom event to notify other components in the same tab
      window.dispatchEvent(new Event('academicProfileUpdated'));
      setIsAutoSaving(false);
    }, 1000);
  }, []);

  const handleChange = (name, value) => {
    const validationError = validateField(name, value);
    
    setValidation(prev => ({
      ...prev,
      [name]: validationError
    }));

    const newProfile = {
      ...profile,
      [name]: value
    };

    setProfile(newProfile);
    
    // Auto-save only if field is valid
    if (!validationError && value !== '') {
      autoSave(newProfile);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const handleClassRankChange = (rankValue) => {
    handleChange('classRank', rankValue);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Manual save - save the profile and close the panel
    localStorage.setItem('academicProfile', JSON.stringify(profile));
    // Dispatch custom event to notify other components in the same tab
    window.dispatchEvent(new Event('academicProfileUpdated'));
    onSave(profile);
    onClose(); // Close the panel after manual save
  };

  const handleKeyDown = (e, nextFieldId) => {
    if (e.key === 'Enter' && nextFieldId) {
      e.preventDefault();
      const nextField = document.getElementById(nextFieldId);
      if (nextField) {
        nextField.focus();
      }
    }
  };

  const tooltipContent = {
    gpa: "Your cumulative GPA on a 4.0 scale. This helps us match you with colleges where students with similar academic performance are admitted.",
    sat: "Your highest SAT score. If you've taken it multiple times, use your best score. This is used for college matching and scholarship opportunities.",
    act: "Your highest ACT composite score. Like SAT, use your best score if you've taken it multiple times.",
    classRank: "Your approximate rank in your graduating class. This provides context for your GPA relative to your school's academic rigor.",
    gender: "Optional demographic information that helps us provide more personalized college recommendations and scholarship opportunities.",
    ethnicity: "Optional demographic information used for scholarship matching and understanding diversity preferences at different colleges."
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
      <motion.div 
        className={`${styles.panel} ${isOpen ? styles.open : ''}`} 
        onClick={(e) => e.stopPropagation()}
        initial={{ x: '100%' }}
        animate={{ x: isOpen ? '0%' : '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Close Button - Top Left */}
        <button 
          className={styles.closeButton} 
          onClick={onClose}
          aria-label="Close profile panel"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTextGroup}>
            <h2 className={styles.title}>Academic Profile</h2>
            <p className={styles.subtitle}>Personalize your college experience</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <motion.div 
              className={styles.progressFill}
              initial={{ width: 0 }}
              animate={{ width: `${completionProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <span className={styles.progressText}>
            {Math.round(completionProgress)}% complete
          </span>
        </div>

        {/* Auto-save indicator */}
        <AnimatePresence>
          {isAutoSaving && (
            <motion.div 
              className={styles.autoSaveIndicator}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className={styles.savingSpinner} />
              Saving...
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        <div className={styles.content}>
          {/* Value Proposition */}
          <div className={styles.valueProposition} style={{ textAlign: 'left' }}>
            <div className={styles.iconContainer}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" fill="currentColor"/>
              </svg>
            </div>
            <div>
              <h3>Why we need this information</h3>
              <p>Your profile helps us provide personalized college recommendations, calculate admission chances, and find scholarships you're eligible for.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} id="profile-form" className={styles.form}>
            {/* Academic Performance Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3>
                  <BookIcon size={20} color="#007AFF" />
                  Academic Performance
                </h3>
                <span className={styles.requiredBadge}>Required</span>
              </div>
              
              <div className={styles.formField}>
                <div className={styles.labelContainer}>
                  <label htmlFor="gpa">GPA (4.0 Scale)</label>
                  <button 
                    type="button"
                    className={styles.tooltipButton}
                    onMouseEnter={() => setShowTooltip('gpa')}
                    onMouseLeave={() => setShowTooltip(null)}
                    aria-label="GPA information"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  {showTooltip === 'gpa' && (
                    <div className={styles.tooltip}>
                      {tooltipContent.gpa}
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  id="gpa"
                  name="gpa"
                  value={profile.gpa}
                  onChange={handleInputChange}
                  onKeyDown={(e) => handleKeyDown(e, 'sat')}
                  placeholder="e.g., 3.75"
                  step="0.01"
                  min="0"
                  max="4.0"
                  className={validation.gpa ? styles.inputError : styles.input}
                  aria-describedby={validation.gpa ? 'gpa-error' : undefined}
                />
                {validation.gpa && (
                  <span id="gpa-error" className={styles.errorMessage} role="alert">
                    {validation.gpa}
                  </span>
                )}
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.formField}>
                  <div className={styles.labelContainer}>
                    <label htmlFor="sat">SAT Score</label>
                    <button 
                      type="button"
                      className={styles.tooltipButton}
                      onMouseEnter={() => setShowTooltip('sat')}
                      onMouseLeave={() => setShowTooltip(null)}
                      aria-label="SAT information"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                    {showTooltip === 'sat' && (
                      <div className={styles.tooltip}>
                        {tooltipContent.sat}
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    id="sat"
                    name="sat"
                    value={profile.sat}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleKeyDown(e, 'act')}
                    placeholder="e.g., 1450"
                    min="400"
                    max="1600"
                    className={validation.sat ? styles.inputError : styles.input}
                    aria-describedby={validation.sat ? 'sat-error' : undefined}
                  />
                  {validation.sat && (
                    <span id="sat-error" className={styles.errorMessage} role="alert">
                      {validation.sat}
                    </span>
                  )}
                </div>

                <div className={styles.formField}>
                  <div className={styles.labelContainer}>
                    <label htmlFor="act">ACT Score</label>
                    <button 
                      type="button"
                      className={styles.tooltipButton}
                      onMouseEnter={() => setShowTooltip('act')}
                      onMouseLeave={() => setShowTooltip(null)}
                      aria-label="ACT information"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                    {showTooltip === 'act' && (
                      <div className={styles.tooltip}>
                        {tooltipContent.act}
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    id="act"
                    name="act"
                    value={profile.act}
                    onChange={handleInputChange}
                    placeholder="e.g., 32"
                    min="1"
                    max="36"
                    className={validation.act ? styles.inputError : styles.input}
                    aria-describedby={validation.act ? 'act-error' : undefined}
                  />
                  {validation.act && (
                    <span id="act-error" className={styles.errorMessage} role="alert">
                      {validation.act}
                    </span>
                  )}
                </div>
              </div>

              {/* Class Rank Section - Use chip style */}
              <div className={styles.formField}>
                <div className={styles.labelContainer}>
                  <label htmlFor="classRank">Class Rank <span className={styles.helpIcon}>?</span></label>
                </div>
                <div className={styles.chipGroup} style={{ justifyContent: 'flex-start' }}>
                  {classRankOptions.filter(opt => opt.value !== 'Unknown').map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`${styles.chip} ${profile.classRank === option.value ? styles.chipActive : ''}`}
                      onClick={() => handleClassRankChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                  <button 
                    type="button"
                    className={`${styles.chip} ${profile.classRank === 'Unknown' ? styles.chipActive : ''}`}
                    onClick={() => handleClassRankChange('Unknown')}
                  >
                    I don't know
                  </button>
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3>
                  <UserIcon size={20} color="#007AFF" />
                  Personal Information
                </h3>
                <span className={styles.optionalBadge}>Optional</span>
              </div>
              <div className={styles.formField}>
                <div className={styles.labelContainer}>
                  <label htmlFor="gender">Gender</label>
                  <button 
                    type="button"
                    className={styles.tooltipButton}
                    onMouseEnter={() => setShowTooltip('gender')}
                    onMouseLeave={() => setShowTooltip(null)}
                    aria-label="Gender information"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  {showTooltip === 'gender' && (
                    <div className={styles.tooltip}>
                      {tooltipContent.gender}
                    </div>
                  )}
                </div>
                <div className={styles.radioGroup} role="radiogroup" aria-labelledby="gender">
                  {genderOptions.map((option) => (
                    <label key={option.value} className={styles.radioOption}>
                      <input
                        type="radio"
                        name="gender"
                        value={option.value}
                        checked={profile.gender === option.value}
                        onChange={handleInputChange}
                        className={styles.radioInput}
                      />
                      <span className={styles.radioLabel}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className={styles.formField}>
                <div className={styles.labelContainer}>
                  <label htmlFor="ethnicity">Ethnicity</label>
                  <button 
                    type="button"
                    className={styles.tooltipButton}
                    onMouseEnter={() => setShowTooltip('ethnicity')}
                    onMouseLeave={() => setShowTooltip(null)}
                    aria-label="Ethnicity information"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  {showTooltip === 'ethnicity' && (
                    <div className={styles.tooltip}>
                      {tooltipContent.ethnicity}
                    </div>
                  )}
                </div>
                <div className={styles.chipGroup}>
                  {ethnicityOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`${styles.chip} ${profile.ethnicity === option.value ? styles.chipActive : ''}`}
                      onClick={() => handleChange('ethnicity', option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Privacy Disclaimer - move above Save Profile button */}
        <div className={styles.privacyNote}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Your information is kept private and secure. We never share your data with third parties.</span>
        </div>
        <div className={styles.footer}>
          <button type="submit" form="profile-form" className={styles.saveBtn}>
            Save Profile
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfilePanel;
